import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Loader2,
  ClipboardCheck,
  MapPin,
  CalendarDays,
  User as UserIcon,
  Paperclip,
  MessageSquare,
  Send,
  Image as ImageIcon,
  FileText,
  Download,
  X,
  Sparkles,
  Lock,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { INSPECTION_STATUSES, INSPECTION_PENALTIES, STATUS_TONES, inspectionStatusMeta, inspectionPenaltyMeta, inspectionTriggerLabel, getInspectionStatus } from "@/lib/inspections";
import { InspectionFormDialog } from "@/components/InspectionFormDialog";

const MAX_ATTACH_BYTES = 20 * 1024 * 1024;
const ACCEPT_MIME = "application/pdf,image/jpeg,image/png,image/webp";

function formatBytes(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateHu(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(d);
  } catch (e) {
    return "—";
  }
}

function formatDateTimeHu(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch (e) {
    return "—";
  }
}

function relativeTimeHu(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "épp most";
  if (diff < 3600) return `${Math.floor(diff / 60)} perce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} órája`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} napja`;
  return formatDateTimeHu(value);
}

function initialsFrom(name = "") {
  const src = (name || "?").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function ResultBadge({ value }) {
  const meta = inspectionStatusMeta(value);
  const Icon = meta.icon;
  return (
    <span
      data-testid={`inspection-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        STATUS_TONES[meta.tone]
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={1.75} />
      {meta.label}
    </span>
  );
}

function PenaltyBadge({ value }) {
  const meta = inspectionPenaltyMeta(value);
  if (!meta) return null;
  return (
    <span
      data-testid={`inspection-penalty-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        STATUS_TONES[meta.tone]
      )}
    >
      {meta.label}
    </span>
  );
}

export default function InspectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [inspection, setInspection] = useState(null);
  const [site, setSite] = useState(null);
  const [company, setCompany] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Composer
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingName, setUploadingName] = useState("");

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  // Close dialog
  const [closeOpen, setCloseOpen] = useState(false);

  // Viewer (overlay)
  const [viewerEvent, setViewerEvent] = useState(null);

  const fetchAll = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const [iRes, eRes] = await Promise.all([
        api.get(`/inspections/${id}`),
        api.get(`/inspections/${id}/events`),
      ]);
      setInspection(iRes.data);
      setEvents(eRes.data);
      if (iRes.data.site_id) {
        try {
          const s = await api.get(`/sites/${iRes.data.site_id}`);
          setSite(s.data);
          if (s.data.company_id) {
            try {
              const c = await api.get(`/companies/${s.data.company_id}`);
              setCompany(c.data);
            } catch (e) {
              setCompany(null);
            }
          }
        } catch (e) {
          setSite(null);
        }
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Nem sikerült betölteni.");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const upsertEvent = (saved) => {
    if (!saved) return;
    setEvents((prev) => {
      const idx = prev.findIndex((e) => e._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [...prev, saved];
    });
  };

  const postComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/inspections/${id}/comments`, { text });
      upsertEvent(data);
      setCommentText("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült elküldeni.");
    } finally {
      setPosting(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_ATTACH_BYTES) {
      toast.error("A fájl túl nagy (max 20 MB).");
      return;
    }
    setUploadingName(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/inspections/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      upsertEvent(data);
      toast.success("Csatolmány feltöltve.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Feltöltés sikertelen.");
    } finally {
      setUploadingName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDeleteEvent = async (event) => {
    if (event.kind === "system") return;
    if (!window.confirm("Biztosan törlöd ezt a bejegyzést?")) return;
    try {
      await api.delete(`/inspections/events/${event._id}`);
      setEvents((prev) => prev.filter((e) => e._id !== event._id));
      toast.success("Bejegyzés törölve.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  const onDeleteInspection = async () => {
    if (!window.confirm("Biztosan törlöd ezt a hatósági ellenőrzést? Ez nem visszavonható."))
      return;
    try {
      await api.delete(`/inspections/${id}`);
      toast.success("Ellenőrzés törölve.");
      navigate(site ? `/sites/${site._id}` : "/companies");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  const sortedEvents = useMemo(() => {
    return [...events].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [events]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-center">
        <p className="text-sm text-muted-foreground">{error || "Nem található."}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/companies">
            <ArrowLeft className="h-3.5 w-3.5" /> Vissza
          </Link>
        </Button>
      </div>
    );
  }

  const title = inspection.subject?.trim()
    ? inspection.subject
    : `${inspection.authority} ellenőrzés`;

  return (
    <div data-testid="inspection-detail-page" className="space-y-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 text-muted-foreground"
        >
          <Link
            to={site ? `/sites/${site._id}` : "/companies"}
            data-testid="back-to-site"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {site ? site.name : "Vissza"}
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-foreground text-background">
            <ClipboardCheck className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Hatósági ellenőrzés
            </p>
            <h1
              data-testid="inspection-title"
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <ResultBadge value={getInspectionStatus(inspection)} />
              <span className="inline-flex items-center gap-1.5">
                <UserIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
                {inspection.authority}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
                {formatDateHu(inspection.inspection_date)}
              </span>
              {site && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />
                  <Link to={`/sites/${site._id}`} className="hover:text-foreground">
                    {site.name}
                  </Link>
                  {company && <span>· {company.name}</span>}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getInspectionStatus(inspection) !== "closed" && (
            <Button
              onClick={() => setCloseOpen(true)}
              data-testid="inspection-close-button"
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <Lock className="h-4 w-4" /> Lezár
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setEditOpen(true)}
            data-testid="inspection-edit-button"
          >
            <Pencil className="h-4 w-4" /> Szerkesztés
          </Button>
          <Button
            variant="outline"
            onClick={onDeleteInspection}
            className="text-destructive hover:text-destructive"
            data-testid="inspection-delete-button"
          >
            <Trash2 className="h-4 w-4" /> Törlés
          </Button>
        </div>
      </div>

      {/* Layout: details left, timeline right */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <DetailCard title="Ellenőrzés adatai" testId="card-inspection-data">
          <DataRow label="Hatóság" value={inspection.authority} />
          <DataRow label="Tárgya" value={inspection.subject} />
          <DataRow label="Ellenőr" value={inspection.inspector_name} />
          <DataRow
            label="Dátum"
            value={formatDateHu(inspection.inspection_date)}
            icon={<CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />}
          />
          <DataRow
            label="Státusz"
            value={<ResultBadge value={getInspectionStatus(inspection)} />}
          />
          <DataRow
            label="Büntetés"
            value={<PenaltyBadge value={inspection.penalty} />}
          />
          {inspection.penalty === "had_penalty" && (
            <>
              <DataRow
                label="Büntetés összege"
                value={
                  inspection.penalty_amount != null
                    ? `${Number(inspection.penalty_amount).toLocaleString("hu-HU")} Ft`
                    : null
                }
              />
              <DataRow
                label="Büntetés indoka"
                value={inspection.penalty_reason}
                multiline
              />
            </>
          )}
          <DataRow
            label="Bejelentés alapján volt?"
            value={inspectionTriggerLabel(inspection.triggered_by_report)}
          />
          <DataRow
            label="Következmények"
            value={inspection.consequences}
            multiline
          />
          <DataRow
            label="Utólagos teendők"
            value={inspection.follow_up_tasks}
            multiline
          />
          <DataRow
            label="Utólagos teendő határideje"
            value={formatDateHu(inspection.follow_up_date)}
          />
          <DataRow label="Megállapítások" value={inspection.findings} multiline />
          <DataRow label="Megjegyzés" value={inspection.notes} multiline />
        </DetailCard>

        <section
          data-testid="inspection-timeline"
          className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]"
        >
          <header className="flex items-center justify-between border-b border-border px-5 py-3">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Időszalag · {sortedEvents.length} bejegyzés
            </h3>
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Sparkles className="h-3 w-3" strokeWidth={1.75} />
              Élő
            </span>
          </header>

          {/* Composer */}
          <div className="border-b border-border px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-foreground text-background text-[10px] font-semibold">
                {initialsFrom(user?.name || user?.email)}
              </div>
              <div className="flex-1 space-y-2">
                <Textarea
                  data-testid="composer-textarea"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Írj megjegyzést az időszalaghoz…"
                  rows={2}
                  className="resize-none"
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      (e.metaKey || e.ctrlKey) &&
                      commentText.trim()
                    ) {
                      e.preventDefault();
                      postComment();
                    }
                  }}
                />
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPT_MIME}
                      className="hidden"
                      data-testid="composer-file-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFile(f);
                      }}
                    />
                    <button
                      type="button"
                      data-testid="composer-attach-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!!uploadingName}
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground disabled:opacity-60"
                    >
                      <Paperclip className="h-3.5 w-3.5" strokeWidth={1.75} />
                      {uploadingName ? `Feltöltés: ${uploadingName}…` : "Fájl csatolása"}
                    </button>
                    <span className="hidden sm:inline">
                      · Ctrl/⌘ + Enter = küldés
                    </span>
                  </div>
                  <Button
                    size="sm"
                    type="button"
                    onClick={postComment}
                    disabled={posting || !commentText.trim()}
                    data-testid="composer-submit"
                  >
                    {posting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Megjegyzés
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Events list */}
          <div className="px-5 py-4">
            {sortedEvents.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
                <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
                  <MessageSquare
                    className="h-4 w-4 text-muted-foreground"
                    strokeWidth={1.75}
                  />
                </div>
                <p className="text-sm font-medium">Még nincs bejegyzés</p>
                <p className="text-xs text-muted-foreground">
                  Adj hozzá egy megjegyzést vagy csatolj egy fájlt — a változások itt jelennek meg.
                </p>
              </div>
            ) : (
              <ol className="relative space-y-5">
                <span
                  aria-hidden
                  className="absolute left-[14px] top-2 bottom-2 w-px bg-border"
                />
                {sortedEvents.map((ev) => (
                  <TimelineItem
                    key={ev._id}
                    event={ev}
                    canDelete={ev.kind !== "system" && (ev.created_by === user?._id)}
                    onDelete={onDeleteEvent}
                    onOpenAttachment={(e) => setViewerEvent(e)}
                  />
                ))}
              </ol>
            )}
          </div>
        </section>
      </div>

      {/* Attachment viewer overlay */}
      <AttachmentViewerDialog
        event={viewerEvent}
        onClose={() => setViewerEvent(null)}
      />

      {/* Edit dialog */}
      <InspectionFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        siteId={inspection.site_id}
        inspection={inspection}
        onSaved={(saved) => {
          if (saved) setInspection((prev) => ({ ...prev, ...saved }));
          fetchAll();
        }}
      />

      {/* Close dialog */}
      <InspectionCloseDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        inspection={inspection}
        onClosed={(saved) => {
          if (saved) setInspection(saved);
          fetchAll();
        }}
      />
    </div>
  );
}

/* ---------- Sub components ---------- */

function TimelineItem({ event, canDelete, onDelete, onOpenAttachment }) {
  const isSystem = event.kind === "system";
  const isAttachment = event.kind === "attachment";
  const isImage = (event.content_type || "").startsWith("image/");

  return (
    <li
      data-testid={`event-${event._id}`}
      className="relative pl-10"
    >
      <span
        className={cn(
          "absolute left-0 top-0 grid h-7 w-7 place-items-center rounded-full border-2 border-[hsl(var(--sipx-elevated))]",
          isSystem
            ? "bg-muted text-muted-foreground"
            : isAttachment
            ? "bg-foreground text-background"
            : "bg-foreground/90 text-background"
        )}
      >
        {isSystem ? (
          <Sparkles className="h-3 w-3" strokeWidth={1.75} />
        ) : isAttachment ? (
          isImage ? (
            <ImageIcon className="h-3 w-3" strokeWidth={1.75} />
          ) : (
            <Paperclip className="h-3 w-3" strokeWidth={1.75} />
          )
        ) : (
          <span className="text-[10px] font-semibold">
            {initialsFrom(event.created_by_name)}
          </span>
        )}
      </span>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pb-1">
        <span className="text-[12.5px] font-medium">
          {event.created_by_name || "Ismeretlen"}
        </span>
        {isSystem && (
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            rendszer
          </span>
        )}
        {isAttachment && (
          <span className="rounded-md bg-foreground/[0.06] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            csatolmány
          </span>
        )}
        <span
          className="text-[11px] text-muted-foreground"
          title={formatDateTimeHu(event.created_at)}
        >
          · {relativeTimeHu(event.created_at)}
        </span>
        {canDelete && (
          <button
            type="button"
            data-testid={`delete-event-${event._id}`}
            onClick={() => onDelete(event)}
            className="ml-auto text-[11px] text-muted-foreground transition-colors hover:text-destructive"
          >
            Törlés
          </button>
        )}
      </div>

      {isAttachment ? (
        <button
          type="button"
          data-testid={`open-attachment-${event._id}`}
          onClick={() => onOpenAttachment(event)}
          className="group flex w-full items-center gap-3 rounded-md border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-foreground/[0.03]"
        >
          {isImage ? (
            <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          ) : (
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          )}
          <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">
            {event.file_name || event.text}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {formatBytes(event.file_size)}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground group-hover:text-foreground">
            Megnyitás
          </span>
        </button>
      ) : (
        <p
          className={cn(
            "whitespace-pre-line text-sm leading-relaxed",
            isSystem && "text-muted-foreground"
          )}
        >
          {event.text}
        </p>
      )}
    </li>
  );
}

function AttachmentViewerDialog({ event, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const open = !!event && event.kind === "attachment";
  const isImage = (event?.content_type || "").startsWith("image/");
  const isPdf = (event?.content_type || "").includes("pdf");

  useEffect(() => {
    if (!open) {
      setBlobUrl("");
      setErr("");
      return;
    }
    let url;
    setLoading(true);
    setErr("");
    api
      .get(`/inspections/events/${event._id}/file`, { responseType: "blob" })
      .then((res) => {
        const ctype =
          event.content_type || res.headers["content-type"] || "application/octet-stream";
        const blob = new Blob([res.data], { type: ctype });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch((e) => {
        setErr(formatApiError(e?.response?.data?.detail) || "Nem sikerült betölteni.");
      })
      .finally(() => setLoading(false));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, event]);

  const onDownload = () => {
    if (!blobUrl || !event) return;
    const a = window.document.createElement("a");
    a.href = blobUrl;
    a.download = event.file_name || "csatolmány";
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="attachment-viewer"
        className="flex max-h-[92vh] w-[92vw] max-w-[92vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
      >
        <DialogHeader className="border-b border-border px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-base tracking-tight">
                {event.file_name || event.text || "Csatolmány"}
              </DialogTitle>
              <DialogDescription className="mt-1 text-xs">
                {event.created_by_name} · {formatDateTimeHu(event.created_at)}
                {event.file_size ? ` · ${formatBytes(event.file_size)}` : ""}
              </DialogDescription>
            </div>
            {blobUrl && (
              <Button
                size="sm"
                variant="outline"
                onClick={onDownload}
                data-testid="attachment-download-button"
              >
                <Download className="h-3.5 w-3.5" />
                Letöltés
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex flex-1 items-stretch justify-center overflow-auto bg-muted/40 p-4">
          {loading ? (
            <div className="flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Fájl betöltése…
            </div>
          ) : err ? (
            <div className="flex items-center justify-center text-sm text-destructive">
              <X className="mr-2 h-4 w-4" /> {err}
            </div>
          ) : isPdf ? (
            <iframe
              data-testid="attachment-pdf-frame"
              title={event.file_name}
              src={blobUrl}
              className="min-h-[70vh] w-full rounded-md border border-border bg-background"
            />
          ) : isImage ? (
            <img
              data-testid="attachment-image"
              src={blobUrl}
              alt={event.file_name}
              className="max-h-[80vh] max-w-full rounded-md object-contain"
            />
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4" strokeWidth={1.5} />
              Előnézet nem elérhető. Töltsd le a fájlt.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailCard({ title, children, testId, className }) {
  return (
    <section
      data-testid={testId}
      className={cn(
        "rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]",
        className
      )}
    >
      <header className="border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </h3>
      </header>
      <dl className="space-y-3 px-5 py-4">{children}</dl>
    </section>
  );
}

function DataRow({ label, value, icon, multiline }) {
  const isEmpty = value === undefined || value === null || value === "";
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "inline-flex items-center gap-1.5 text-sm",
          isEmpty && "text-muted-foreground/70",
          multiline && "whitespace-pre-line"
        )}
      >
        {icon && !isEmpty && <span className="text-muted-foreground">{icon}</span>}
        {isEmpty ? "—" : value}
      </dd>
    </div>
  );
}

/* ---------- Close Dialog ---------- */

function InspectionCloseDialog({ open, onOpenChange, inspection, onClosed }) {
  const [penalty, setPenalty] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [consequences, setConsequences] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setPenalty(inspection?.penalty || "");
    setAmount(
      inspection?.penalty_amount != null ? String(inspection.penalty_amount) : ""
    );
    setReason(inspection?.penalty_reason || "");
    setConsequences(inspection?.consequences || "");
    setFollowUp(inspection?.follow_up_tasks || "");
    setErr("");
  }, [open, inspection]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!penalty) {
      setErr("Válaszd ki, hogy volt-e büntetés.");
      return;
    }
    if (penalty === "had_penalty") {
      const amt = parseFloat(amount);
      if (!amt || amt <= 0) {
        setErr("Büntetés esetén kötelező megadni az összeget.");
        return;
      }
      if (!reason.trim()) {
        setErr("Büntetés esetén kötelező megadni az indokot.");
        return;
      }
    }
    if (!consequences.trim()) {
      setErr("A következmények megadása kötelező.");
      return;
    }
    if (!followUp.trim()) {
      setErr("Az utólagos teendők megadása kötelező.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        penalty,
        penalty_amount: penalty === "had_penalty" ? parseFloat(amount) : null,
        penalty_reason:
          penalty === "had_penalty" ? reason.trim() || null : null,
        consequences: consequences.trim(),
        follow_up_tasks: followUp.trim(),
      };
      const { data } = await api.post(
        `/inspections/${inspection._id}/close`,
        payload
      );
      toast.success("Ellenőrzés lezárva.");
      onOpenChange(false);
      if (onClosed) onClosed(data);
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) || "Lezárás sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="inspection-close-dialog"
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700">
              <Lock className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
            Ellenőrzés lezárása
          </DialogTitle>
          <DialogDescription>
            Add meg az alábbi mezőket. Lezárás után ezeket már nem lehet módosítani.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Büntetés *
            </label>
            <Select value={penalty} onValueChange={setPenalty}>
              <SelectTrigger data-testid="inspection-close-penalty">
                <SelectValue placeholder="Válassz…" />
              </SelectTrigger>
              <SelectContent>
                {INSPECTION_PENALTIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {penalty === "had_penalty" && (
            <div className="grid grid-cols-1 gap-3 rounded-lg border border-rose-200 bg-rose-50/40 p-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Büntetés összege (Ft) *
                </label>
                <Input
                  type="number"
                  min="0"
                  step="1000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="inspection-close-amount"
                  placeholder="Pl. 150000"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Büntetés indoka *
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  data-testid="inspection-close-reason"
                  placeholder="Pl. Élelmiszer-higiéniai szabályok megsértése"
                />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Következmények *
            </label>
            <Textarea
              value={consequences}
              onChange={(e) => setConsequences(e.target.value)}
              rows={3}
              data-testid="inspection-close-consequences"
              placeholder="Milyen következmények érintik a céget / telephelyet?"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Utólagos teendők *
            </label>
            <Textarea
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              rows={3}
              data-testid="inspection-close-followup"
              placeholder="Milyen lépéseket / pótlásokat kell elvégezni?"
            />
          </div>
          {err && (
            <div
              data-testid="inspection-close-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="inspection-close-cancel"
            >
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="inspection-close-submit"
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Lezárás…
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4" /> Lezárás
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
